import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  Divider,
  Grid,
  Chip,
} from "@mui/material";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchableSelect from "../../../common/SearchableSelect";

const DeliverySummaryPanel = ({
  t,
  prescriptionAssign,
  locationType,
  locationName,
  stockCode,
  pickupCode,
  searchedItems,
  addressLine1,
  addressLine2,
  city,
  state,
  zip,
  validatedLocation,
  boxes = [],
  locationtype_id,
  onLocationChange,
  onCounterLocationChange,
  expirationDays,
}) => {
  const counterBoxes = boxes ? boxes.filter(box => {
    const boxLocationTypeId = box.location_type_id || box.locationtype_id;
    return boxLocationTypeId === locationtype_id;
  }) : [];
  
  const storageLocationDisplay = validatedLocation && (validatedLocation.location || validatedLocation.location_code || "N/A");
  
  const handleLocationChange = (event) => {
    const selectedId = event.target.value;
    const box = counterBoxes.find((b) => b.id === selectedId);
    if (box && onCounterLocationChange) {
      onCounterLocationChange(box.name);
    }
  };

  const locationItems = counterBoxes.map((box) => ({
    id: box.id,
    name: box.name,
  }));
  const selectedLocation = counterBoxes.find((b) => b.name === locationName);
  const selectedLocationId = selectedLocation && selectedLocation.id ? selectedLocation.id : "";

  const assignedDateDisplay =
    (searchedItems && searchedItems.assignedDate) ||
    new Date().toLocaleDateString();
  const stockedDateDisplay =
    (searchedItems && searchedItems.stockedDate) || "N/A";
  const existingExpires =
    searchedItems &&
    (searchedItems.expiresDate || searchedItems.rx_expiration_date);
  let expiringOnDisplay = existingExpires || "N/A";
  if (!existingExpires) {
    const days = parseInt(expirationDays, 10);
    if (days && days > 0 && !Number.isNaN(days)) {
      const base = searchedItems && searchedItems.assignedDate
        ? new Date(searchedItems.assignedDate)
        : new Date();
      if (!Number.isNaN(base.getTime())) {
        base.setDate(base.getDate() + days);
        expiringOnDisplay = base.toLocaleDateString();
      }
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2.5,
          pb: 2,
          borderBottom: "2px solid #f1f5f9",
        }}
      >
        <LocalPharmacyIcon
          sx={{
            color: "#0369a1",
            fontSize: 24,
            mr: 1.5,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: "#1e293b",
            letterSpacing: "0.01em",
          }}
        >
          Delivery Information
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "0.875rem",
              mb: 1,
              color: "#475569",
            }}
          >
            {t("Assign.DeliveryType")}
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={locationType}
            disabled
            InputLabelProps={{ style: { fontSize: "0.875rem" } }}
            sx={{
              fontSize: "0.875rem",
              backgroundColor: "#ffffff",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                fontSize: "0.875rem",
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e2e8f0",
                  borderWidth: 2,
                },
                "&:hover": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#0369a1",
                  },
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "0.875rem",
              mb: 1,
              color: "#475569",
            }}
          >
            {t("Assign.Location")}
          </Typography>
          <SearchableSelect
            label={t("Assign.Location")}
            items={locationItems}
            value={selectedLocationId}
            onChange={handleLocationChange}
            placeholder="Search locations..."
            id="delivery-summary-location-select"
            fullWidth={true}
            disabled={!prescriptionAssign}
            variant="standard"
            showLabel={false}
            sx={{
              "& .MuiInputBase-root": {
                fontSize: "0.875rem",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                border: "2px solid #e2e8f0",
                boxSizing: "border-box",
                minHeight: 40,
                height: 40,
                "& .MuiSelect-select": {
                  padding: "8px 14px",
                },
                "&:hover": {
                  borderColor: "#0369a1",
                },
              },
            }}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: 3,
          p: 2.5,
          background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
          borderRadius: 2,
          border: "1px solid #e2e8f0",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography
                variant="body2"
                component="span"
                sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#475569" }}
              >
                {t("Assign.StockCode")}:
              </Typography>
              <Chip
                label={stockCode || "N/A"}
                size="small"
                sx={{
                  height: 28,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  bgcolor: "#e0f2fe",
                  color: "#0369a1",
                  border: "1px solid #0369a1",
                  "& .MuiChip-label": {
                    px: 1.25,
                    fontSize: "0.8125rem",
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography
                variant="body2"
                component="span"
                sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#475569" }}
              >
                {t("Assign.PUCode")}:
              </Typography>
              <Chip
                label={pickupCode || "N/A"}
                size="small"
                sx={{
                  height: 28,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  bgcolor: "#e0f2fe",
                  color: "#0369a1",
                  border: "1px solid #0369a1",
                  "& .MuiChip-label": {
                    px: 1.25,
                    fontSize: "0.8125rem",
                  },
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
              <Box component="span" sx={{ fontWeight: 700, color: "#475569" }}>
                {t("Assign.StorageLocation") || "Storage Location"}:
              </Box>{" "}
              <Box component="span" sx={{ color: "#0f172a", fontWeight: 500 }}>
                {storageLocationDisplay}
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            {locationName && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    mb: 1,
                    color: "#475569",
                  }}
                >
                  Address
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    color: "#1e293b",
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                >
                  {addressLine1 || "-"}
                </Typography>
                {addressLine2 && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      color: "#1e293b",
                      mb: 0.5,
                      fontWeight: 500,
                    }}
                  >
                    {addressLine2}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    color: "#1e293b",
                    fontWeight: 500,
                  }}
                >
                  {city && state && zip ? `${city}, ${state} ${zip}` : "-"}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3, borderColor: "#e2e8f0" }} />

      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2.5,
            pb: 2,
            borderBottom: "2px solid #f1f5f9",
          }}
        >
          <CalendarTodayIcon
            sx={{
              color: "#0369a1",
              fontSize: 24,
              mr: 1.5,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: "#1e293b",
              letterSpacing: "0.01em",
            }}
          >
            Status Information
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontSize: "0.875rem", mb: 1.5 }}>
          <Box component="span" sx={{ fontWeight: 700, color: "#475569" }}>
            {t("Global.Prescriptions.Status.Assigned")}:
          </Box>{" "}
          <Box component="span" sx={{ color: "#0f172a", fontWeight: 500 }}>
            {assignedDateDisplay}
          </Box>
        </Typography>
        <Typography variant="body2" sx={{ fontSize: "0.875rem", mb: 1.5 }}>
          <Box component="span" sx={{ fontWeight: 700, color: "#475569" }}>
            {t("Global.Prescriptions.Status.Stocked")}:
          </Box>{" "}
          <Box component="span" sx={{ color: "#0f172a", fontWeight: 500 }}>
            {stockedDateDisplay}
          </Box>
        </Typography>
        <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
          <Box component="span" sx={{ fontWeight: 700, color: "#475569" }}>
            {t("Assign.ExpiringOn", { defaultValue: "Expiring on" })}:
          </Box>{" "}
          <Box component="span" sx={{ color: "#0f172a", fontWeight: 500 }}>
            {expiringOnDisplay}
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

DeliverySummaryPanel.propTypes = {
  t: PropTypes.func.isRequired,
  prescriptionAssign: PropTypes.bool,
  locationType: PropTypes.string,
  locationName: PropTypes.string,
  stockCode: PropTypes.string,
  pickupCode: PropTypes.string,
  searchedItems: PropTypes.object,
  addressLine1: PropTypes.string,
  addressLine2: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
  validatedLocation: PropTypes.object,
  boxes: PropTypes.array,
  locationtype_id: PropTypes.string,
  onLocationChange: PropTypes.func,
  onCounterLocationChange: PropTypes.func,
  expirationDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DeliverySummaryPanel.defaultProps = {
  prescriptionAssign: true,
  locationType: "",
  locationName: "",
  stockCode: "",
  pickupCode: "",
  searchedItems: {},
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
  validatedLocation: null,
  boxes: [],
  locationtype_id: null,
  onLocationChange: null,
  onCounterLocationChange: null,
  expirationDays: null,
};

export default DeliverySummaryPanel;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/components/DeliverySummaryPanel.jsx